<template>
  <div class="w-full flex md:items-center">
    <div class="flex-grow pr-10">
      <div class="flex items-center pr-20">
        <div class="w-4 rounded-8 h-40 block mr-10" :style="userColor" />
        <div>
          <div class="flex items-start">
            <div class="relative">
              <p class="truncate">{{ member.firstName }} {{ member.lastName }}</p>
              <span
                v-if="member.role === 'admin'"
                class="text-brand border shadow-brand font-medium rounded-4 ml-10 px-7 text-10 -mt-3 absolute top-0 left-full"
              >admin</span>
            </div>
          </div>
          <p class="opacity-70 text-14 truncate pr-20">{{ member.email }}</p>
        </div>
      </div>
    </div>
    <!-- SPACES -->
    <div class="w-150 flex relative items-center">
      <p class="mr-5">{{ member.books.length }} Spaces</p>
      <ButtonModule
        :has-hover="true"
        prefix="icon-info_outline text-main-color"
        class="text-app-color top-0 bottom-0 right-0 p-0"
        @click="toggleTeamModal"
      />
      <InfoDropdown v-if="showTeamMembers" title="Spaces" class="-right-10 mt-25 -ml-20 relative" @showDropdown="closeTeamModal">
        <div class="pb-8 px-12 overflow-y-auto">
          <p v-for="book in member.books" :key="book.id" class="mb-4 text-15">
            {{ book.title }}
          </p>
        </div>
      </InfoDropdown>
    </div>

    <!-- ROLE -->
    <div class="w-full max-w-170">
      <TeamRolesSelect
        :disabled="memberIsOwner || isRegularUser"
        :role="member.role"
        @setRole="setRole"
      />
    </div>

    <!-- BILLING -->
    <div class="w-70 text-center">
      <CheckboxModule
        class="justify-center"
        :disabled="member.role === 'admin' || memberIsOwner || !isAdmin"
        :model-value="member.hasBillingAccess"
        @update:modelValue="changeBillingAccess"
      />
    </div>

    <!-- DELETE -->
    <div class="w-100 text-center">
      <ButtonModule
        v-if="!memberIsOwner && currentMember.id !== member.id"
        :disabled="!isAdmin"
        :has-hover="true"
        prefix="icon-delete text-main-color"
        class="text-app-color top-0 bottom-0 right-0 p-0"
        @click="deleteMember"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { useStore } from 'vuex'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import InfoDropdown from '@/components/shared/InfoDropdown'
import CheckboxModule from '@/components/shared/form-controls/CheckboxModule'
import TeamRolesSelect from '@/components/team/TeamRolesSelect'

import userColorMixin from '@/mixins/userColorMixin'
import { teamsService } from '@/services'

export default defineComponent({
  components: { ButtonModule, InfoDropdown, CheckboxModule, TeamRolesSelect },

  props: {
    member: Object
  },

  setup (props) {
    const { state, getters, dispatch } = useStore()
    const isDarkMode = computed(() => getters.isDarkMode)
    const { userColor } = userColorMixin(props.member.color)

    const showTeamMembers = ref(false)
    const menuIsOpen = ref(false)

    const team = computed(() => state.team.team)
    const currentMember = computed(() => state.team.member)

    const memberIsOwner = computed(() => team.value.userId === props.member.userId)
    const isAdmin = computed(() => getters.isAdmin)
    const isRegularUser = computed(() => getters.isRegularUser)

    function toggleMenu (value) {
      menuIsOpen.value = value
    }

    function toggleTeamModal () {
      setTimeout(() => {
        showTeamMembers.value = !showTeamMembers.value
      }, 100)
    }

    function closeTeamModal () {
      showTeamMembers.value = false
    }

    function changeBillingAccess (billingAccess) {
      dispatch('updateBillingAccess', {
        id: props.member.id,
        billingAccess: billingAccess
      })
    }

    function setRole (role) {
      dispatch('updateMemberRole', {
        id: props.member.id,
        role: role
      })
    }

    function deleteMember () {
      teamsService.deleteMember(props.member.id).then(() => dispatch('getMembers'))
    }

    return {
      showTeamMembers,
      menuIsOpen,
      isDarkMode,
      memberIsOwner,
      isAdmin,
      userColor,
      isRegularUser,
      currentMember,
      // functions
      toggleMenu,
      toggleTeamModal,
      closeTeamModal,
      deleteMember,
      changeBillingAccess,
      setRole
    }
  }
})
</script>
